<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import { useI18n } from 'vue-i18n';
import { useCode } from '@composables/code';

/**
 * Строка с ID пользователя в кабинете.
 *
 * Той же высоты и с таким же кругом под иконку, что карточки привязанных
 * аккаунтов под ней: три карточки подряд должны читаться одним блоком.
 * Копирование — отдельной кнопкой справа, а не тапом по всей строке: строка
 * никуда не ведёт, и без кнопки не видно, что с ней можно что-то сделать.
 */
const props = defineProps<{
	id?: number | string | null;
}>();

const { t } = useI18n();
const { copyToClipboard } = useCode();

const copy = () => {
	if (props.id === null || props.id === undefined) return;
	copyToClipboard(String(props.id));
};
</script>

<template>
	<div class="part-user-id">
		<span class="part-user-id__icon-wrap">
			<core-icon
				name="account"
				viewBox="0 0 18 18"
				class="part-user-id__icon"
			/>
		</span>
		<span class="part-user-id__text">ID: {{ id }}</span>
		<button
			type="button"
			class="part-user-id__copy"
			:aria-label="t('accountIndex.copy_id')"
			:title="t('accountIndex.copy_id')"
			@click="copy"
		>
			<core-icon name="copy" viewBox="0 0 18 18" />
		</button>
	</div>
</template>

<style lang="scss">
.part-user-id {
	display: flex;
	align-items: center;
	gap: 1.2rem;
	min-height: 5.2rem;
	padding: 0.5rem 1rem 0.5rem 1.2rem;
	border-radius: 1.6rem;
	background-color: var(--color-panel);
	border: 1px solid rgba(var(--color-active-rgb), 0.12);
	color: var(--color-text);

	@include min-desktop {
		min-height: 6.2rem;
		padding: 0.8rem 1.4rem 0.8rem 1.6rem;
		gap: 1.6rem;
	}

	&__icon-wrap {
		flex: 0 0 auto;
		width: 3.2rem;
		height: 3.2rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background-color: var(--color-white);
		fill: var(--color-active);

		@include theme-dark {
			background-color: rgba(var(--color-active-rgb), 0.06);
		}
	}

	&__icon {
		width: 1.6rem;
		height: 1.6rem;

		@include min-desktop {
			width: 1.8rem;
			height: 1.8rem;
		}
	}

	&__text {
		flex: 1 1 auto;
		min-width: 0;
		font-size: 1.4rem;
		font-weight: 500;

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	// Иконка без подложки, как у отвязки аккаунта: второстепенное действие
	&__copy {
		flex: 0 0 auto;
		width: 4.2rem;
		height: 4.2rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 50%;
		background: transparent;
		fill: var(--color-active);
		opacity: 0.7;
		cursor: pointer;
		transition: opacity 0.2s ease, background-color 0.2s ease;

		svg {
			width: 1.6rem;
			height: 1.6rem;
		}

		&:hover {
			opacity: 1;
			background-color: rgba(var(--color-active-rgb), 0.08);
		}
	}
}
</style>
